import { when } from 'mobx'
import { api } from 'store/api'
import { controller } from 'store/controller'
import { ExistingStore } from 'store/generation/ExistingStore'
import { buildGenerateRequest as buildRequest, GenerateParams, getSelectedImages } from 'store/generation/generateRequest'
import { GeneratingStore } from 'store/generation/GeneratingStore'
import { InferenceStore, NewInferenceData } from 'store/inference/InferenceStore'
import { me } from 'store/me'
import { opt, STAND_BANNER } from 'store/opt'
import ProfileStore from 'store/profile/ProfileStore'
import { stat } from 'store/stat'
import { store } from 'store/store'
import { fromItem, fromOfferItem, GenerateRequest, InferenceResponse, ItemData } from 'type/Inference'
import { postMessage, sendEvent } from 'util/message'
import { delay } from 'util/promise'
import { parseUrl } from 'util/url'

export type { GenerateParams } from 'store/generation/generateRequest'

function sendGenStat(request: GenerateRequest, data: NewInferenceData, response: InferenceResponse, combo_cart: boolean) {
  const { item_id, combo_item_id, identity_id } = request
  const { item, identity_mode } = data
  const { collection } = item
  const { inference_id } = response
  const source_inference_id = data.source?.inference_id
  stat.gen(item_id, collection, identity_id, inference_id, identity_mode, combo_item_id, source_inference_id, combo_cart)
}

function sameGen(inference: InferenceStore, item: ItemData, combo: ItemData | undefined, identity_id: string) {
  return inference.item_id === item.item
    && inference.items[1]?.item === combo?.item
    && inference.identity_id === identity_id
}

function samePics(profile: ProfileStore, inference: InferenceStore) {
  const a = parseUrl(profile.common.pic)
  const b = parseUrl(inference.pic)
  return a?.pathname === b?.pathname
}

export async function generateLook(params: GenerateParams = {}) {
  const profile = store.profile
  if (!profile) throw new Error('no profile')

  const open_item_id = store.open_item_id
  if (!store.available) throw new Error('no limit')

  const { rec, source } = params
  const single: ItemData | undefined = fromItem(params?.single?.json) ?? source?.items[0]
  const item: ItemData | undefined = single ?? fromItem(store.item?.json)
  const combo: ItemData | undefined = single ? undefined : rec ? fromOfferItem(rec) : fromItem(store.comboItem?.json)
  const combo_item_id = combo?.external_item_id || combo?.item
  const combo_cart = !rec && !!combo_item_id
  const video = !!source

  if (!item) throw new Error('no item')

  const identity_id = profile.identity_id
  const identity_mode = profile.form.mode
  const pic = profile.common.pic
  const selected_item_images = getSelectedImages(params, { item: store.item, comboItem: store.comboItem })
  const data: NewInferenceData = { identity_id, identity_mode, pic, selected_item_images, item, combo, source }

  if (opt.single_gen) {
    const inference = store.inferences.inferences.find(it => sameGen(it, item, combo, identity_id))
    if (inference && samePics(profile, inference)) {
      store.existing = new ExistingStore(inference)
      store.screen = 'existing'
      store.state = 'max'
      return
    }
  }

  async function go() {
    const BANNER_TIMEOUT = 15_000
    const start = Date.now()
    const gen = new GeneratingStore(video)

    try {
      store.generating = gen
      store.screen = 'generating'
      store.inferences.tab = opt.tsum ? 'new' : 'all'

      const inference = InferenceStore.fromEmpty()
      inference.beforeCreate(data)

      store.generating.inference = inference
      store.inferences.addNewInference(inference)

      const createInference = async () => {
        let request: GenerateRequest | undefined
        try {
          await profile.ensureSaved()

          request = await buildGenerateRequest(combo_item_id)

          postMessage('on-generation-start', {
            item_id: request.item_id,
            combo_item_id: request.combo_item_id,
            video,
          })

          const response = video
            ? await api.generateVideo(source.inference_id)
            : await api.generateInference(request)

          sendGenStat(request, data, response, combo_cart)
          inference.afterCreate(response)
          sendEvent('on-create-outfit', {
            item_id: request.item_id,
            combo_item_id: request.combo_item_id,
            video,
          })

          postMessage('on-generation-start-success', {
            item_id: request.item_id,
            combo_item_id: request.combo_item_id,
            video,
            inference_id: response.inference_id,
          })
        } catch (e) {
          inference.onError()

          postMessage('on-generation-start-error', {
            item_id: request?.item_id,
            combo_item_id: request?.combo_item_id,
            video,
            error: e instanceof Error ? e.message : String(e),
          })
          throw e
        }
      }

      const creating = createInference()

      if (opt.stand && STAND_BANNER) {
        await creating
        await when(() => inference.complete)
        await delay(start + BANNER_TIMEOUT - Date.now())
      }

      controller.openInference(inference, 'new')
      store.checkLimit()
      store.showUserDataBannerMaybe()

      await creating
    } catch (e) {
      console.error('generation failed', e)
      gen.error = true

      if (!store.generating?.inference) {
        postMessage('on-generation-start-error', {
          video,
          error: e instanceof Error ? e.message : String(e),
        })
      }
    }
  }

  async function buildGenerateRequest(combo_item_id?: string): Promise<GenerateRequest> {
    const { user_id, manager_id } = me
    return buildRequest({
      user_id,
      manager_id,
      user_email: me.email,
      user_ext_id: me.external_user,
      identity_id: profile.identity_id,
      vendor: opt.options.vendor,
      gemini_model: opt.options.gemini_model,
      high: store.tg,
      item,
      open_item_id,
      combo_item_id,
      combine_full: opt.combine_full,
      type: profile.type,
    })
  }

  await go()
}
